// Court-aware watch widget — full per-court purchase, IN-WIDGET. Browse data is anonymous; the purchase
// is a DIRECT authenticated call to /api/timeslots/purchase (cross-domain login via the Auth class). The
// API is embed-origin aware, so Stripe's success/cancel URLs come back to THIS venue site. Mirrors the
// web /zone page: the day is 96 one-hour windows (4 per hour at :00/:15/:30/:45) grouped into hourly
// cards; PAST slot → recording only; CURRENT/FUTURE slot → Live / Recording / Bundle (20% off).
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { SessionStore } from '../store';
import type { EmbedConfig, RecentGame, ThemeOptions, ZoneDetail, WatchSlot, TimeSlotPurchaseResult, StreamSession } from '../types';
import { createRoot, h, money, renderError, renderLoading, footer, clear, receipt, signOutRow, showSalesPaused, blockIfSalesPaused, taxFor, totalWithTax, moneyPlusTax, taxNoteEl } from '../ui';
import { attachScoreOverlay } from './score-overlay';
import { sitePage } from '../pages';
import { track, trackIds, withWa } from '../analytics';

type PType = 'LiveStream' | 'Recording' | 'Bundle';
interface PendingBuy { t: 'watch'; zoneId: string; slotStart: string; type: PType; matchId?: string; }
/** The buy callback the cards call: the option clicked, plus the element holding its buttons (for the tax confirm step). */
type BuyFn = (s: WatchSlot, t: PType, anchor?: HTMLElement) => void;

/** The sales-tax rate to quote: the embed config's, else the zone payload's, else 0 (nothing shown). */
function taxPct(cfg: EmbedConfig, zone?: { salesTaxPercent?: number | null } | null): number {
  const v = cfg.salesTaxPercent ?? zone?.salesTaxPercent ?? 0;
  return Number(v) > 0 ? Number(v) : 0;
}
/** Pre-tax list price of one option on a court (the server re-prices at purchase; this is what the button says). */
function priceOf(zone: ZoneDetail, type: PType): number {
  return type === 'LiveStream' ? zone.liveStreamPrice : type === 'Recording' ? zone.recordingPricePerHour : bundlePrice(zone);
}
const typeLabel = (type: PType) => type === 'LiveStream' ? 'Live stream' : type === 'Recording' ? 'Recording' : 'Live + recording bundle';

/**
 * With sales tax, the option buttons are the price LIST (pre-tax, beside the one rate note), so the
 * buy ACTION gets its own step that shows the real total before Stripe does:
 *   "Live stream · 7:00 PM – 8:00 PM" / "$5.00 + $0.41 sales tax" / [Buy — $5.41] [Back]
 * Rendered in place of the option buttons; Back puts them back. Not used at all when the venue
 * doesn't tax — the buttons then go straight to checkout exactly as before.
 */
function confirmWithTax(anchor: HTMLElement, what: string, pre: number, pct: number, go: () => void): void {
  const prev = Array.from(anchor.childNodes);
  const prevDisplay = anchor.style.display;
  const restore = () => { clear(anchor); anchor.style.display = prevDisplay; anchor.append(...prev); };
  clear(anchor);
  anchor.style.display = 'block';
  const buyBtn = h('button', { class: 'e365-btn', type: 'button', style: 'margin-top:6px' },
    `Buy — ${money(totalWithTax(pre, pct))}`) as HTMLButtonElement;
  buyBtn.addEventListener('click', () => { buyBtn.disabled = true; buyBtn.textContent = 'Setting up…'; go(); });
  const back = h('button', { class: 'e365-opt', type: 'button', style: 'margin-top:6px' }, 'Back') as HTMLButtonElement;
  back.addEventListener('click', restore);
  anchor.append(
    h('div', { style: 'font-weight:600; font-size:.85rem' }, what),
    h('div', { class: 'e365-muted', style: 'font-size:.8rem' }, moneyPlusTax(pre, pct)),
    buyBtn, back,
  );
}

/** A row from GET /api/purchases (the user's owned streams/recordings). */
interface MyPurchase {
  type?: string | number;
  isPaid?: boolean;
  zoneName?: string | null;
  venueId?: string | null;
  venueName?: string | null;
  streamingSessionId?: string | null;
  accessStart?: string | null;
  accessEnd?: string | null;
  recordingReady?: boolean;
  orderNumber?: string | null;  // receipt
  amount?: number | null;       // amount paid, PRE-tax (the list price)
  taxAmount?: number | null;    // sales tax paid on top of amount; 0/absent (older API) = none
  currency?: string | null;
  paidAt?: string | null;       // receipt date
}

function platformBase(cfg: EmbedConfig): string {
  return (cfg.loginUrl || '').replace(/\/embed-login\.html.*$/, '').replace(/\/$/, '');
}
// Route to the GAME-bounded recording page (scoreboard start→end, flat price). It auto-falls-back to the
// 1-hour slot recording for a game that wasn't scoreboard-tracked, so this is always the right entry point.
// `return` = this site's payment-success page, so the customer lands back here (with the player) after paying.
const goRecording = (cfg: EmbedConfig, matchId: string) => {
  const inWidget = document.querySelector('[data-365="watch"]')?.shadowRoot?.querySelector('.e365');
  if (blockIfSalesPaused(cfg, inWidget ?? document.body)) return;
  const ret = new URL(sitePage('paymentSuccess'), location.href).href;
  track('recording_start', 'watch');
  window.location.href = withWa(`${platformBase(cfg)}/buy-game-recording/${matchId}?return=${encodeURIComponent(ret)}`);
};

function readZoneParam(): string | null {
  try { return new URLSearchParams(location.search).get('zone'); } catch { return null; }
}
// LOCAL calendar date (not toISOString(), which is UTC — after ~7pm Central that rolls to tomorrow
// and the widget would show the wrong day's schedule + hide the "now" card).
function todayLocal(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function tzOffsetMin(): number { return new Date().getTimezoneOffset(); }
// API slot times are naive UTC (e.g. "2026-06-24T13:00:00", no 'Z'); force UTC parsing so the
// browser-local conversion (hour grouping, labels) is correct instead of off by the tz offset.
const asDate = (s: string): Date => new Date(/[zZ]|[+-]\d\d:?\d\d$/.test(s) ? s : `${s}Z`);
const fmtT = (utc: string) => asDate(utc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
const fmtDateShort = (s: string): string => { try { return asDate(s).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }); } catch { return ''; } };
const fmtHour = (h24: number) => new Date(2000, 0, 1, h24).toLocaleTimeString([], { hour: 'numeric' });
const bundlePrice = (z: ZoneDetail) => Math.round((z.liveStreamPrice + z.recordingPricePerHour) * 0.8 * 100) / 100;

export function mountWatch(
  mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions, _store: SessionStore,
): void {
  const { root } = createRoot(mount, theme);
  if (!cfg.watchEnabled) { renderError(root, 'Live streams aren’t available for this venue right now.'); return; }
  showSalesPaused(root, cfg);

  // Returned from Stripe to the venue site's /payment/success → confirm the purchase + show the player.
  const params = new URLSearchParams(location.search);
  const purchaseId = params.get('purchase_id');
  const stripeSession = params.get('session_id');
  if (purchaseId && stripeSession) {
    void renderPaymentSuccess(root, cfg, api, auth, purchaseId, stripeSession);
    return;
  }

  // Resume a purchase the user started before the cross-domain login redirect (loader already ran completeReturn()).
  const pending = auth.peekPending<PendingBuy>();
  if (pending && pending.t === 'watch') {
    auth.clearPending();
    if (auth.isLoggedIn) {
      renderLoading(root, 'Completing your purchase…');
      void doPurchase(root, cfg, api, auth, pending.zoneId, pending.slotStart, pending.type, todayLocal(), pending.matchId);
      return;
    }
  }
  void load(root, cfg, api, auth, readZoneParam(), todayLocal());
}

// ── data-365="my-streams" — a dedicated, login-gated library of the customer's purchases for this venue ──
export function mountMyStreams(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  if (!cfg.watchEnabled) { renderError(root, 'Live streams aren’t available for this venue right now.'); return; }
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'my-streams') auth.clearPending();
  void renderMyStreams(root, cfg, api, auth);
}

async function renderMyStreams(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clear(root);
  root.append(h('h3', undefined, 'My Streams'));
  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to see the streams & recordings you’ve purchased.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-streams' }));
    root.append(login, footer());
    return;
  }
  const host = h('div');
  root.append(host);
  renderLoading(host, 'My Streams');
  const status = await renderMyPurchases(host, api, { id: cfg.venueId, name: cfg.venueName }, (sessionId) => {
    void showEntitled(root, cfg, api, auth, sessionId, null, null, 'Watch');
  });
  if (status === 'error') {
    clear(host);
    host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your streams right now. Please try again.'));
    const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
    retry.addEventListener('click', () => void renderMyStreams(root, cfg, api, auth));
    host.append(retry);
  } else if (status === 'empty') {
    clear(host);
    host.append(
      h('div', { class: 'e365-alert info' }, 'You haven’t purchased any streams or recordings for this venue yet.'),
      h('a', { href: sitePage('watch'), class: 'e365-btn', style: 'display:flex; text-decoration:none; margin-top:8px' }, 'Browse live & recordings'),
    );
  }
  root.append(
    signOutRow(auth.profile?.email, () => { auth.logout(); void renderMyStreams(root, cfg, api, auth); }),
    footer(),
  );
}

async function load(
  root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, zoneId: string | null, date: string, notice?: string,
): Promise<void> {
  if (!zoneId) { await renderZonePicker(root, cfg, api, auth, date); return; }

  renderLoading(root, 'Watch live & recordings');
  let zone: ZoneDetail;
  try {
    zone = await api.get<ZoneDetail>(`/api/browse/zones/${zoneId}?date=${date}&tzOffset=${tzOffsetMin()}`);
  } catch {
    renderError(root, 'We couldn’t load this court’s streams. Please try again.', () => void load(root, cfg, api, auth, zoneId, date));
    return;
  }
  let games: RecentGame[] = [];
  let recent: RecentGame[] = [];
  let liveGames: LiveGame[] = [];
  try { games = (await api.get<RecentGame[]>(`/api/browse/zones/${zoneId}/completed-games?date=${date}`)) || []; } catch { /* ignore */ }
  try { recent = (await api.get<RecentGame[]>(`/api/browse/venues/${zone.venueId}/recent-games?count=6`)) || []; } catch { /* ignore */ }
  try { liveGames = (await api.get<LiveGame[]>(`/api/browse/venues/${zone.venueId}/live-games`)) || []; } catch { /* ignore */ }
  renderCourt(root, cfg, api, auth, zone, zoneId, date, games, recent, liveGames, notice);
}

/** A row from GET /api/browse/venues/{id}/live-games — a game being scored right now. */
interface LiveGame {
  matchId: string;
  homeTeam: string;
  awayTeam: string;
  homeScore?: number | null;
  awayScore?: number | null;
  title?: string | null;
  zoneId: string;
  zoneName?: string | null;
}

function renderCourt(
  root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth,
  zone: ZoneDetail, zoneId: string, date: string, games: RecentGame[], recent: RecentGame[], liveGames: LiveGame[], notice?: string,
): void {
  clear(root);
  const pct = taxPct(cfg, zone);
  const buy = (slot: WatchSlot, type: PType, anchor?: HTMLElement, matchId?: string, what?: string) => {
    const go = () => void startPurchase(root, cfg, api, auth, zoneId, slot, type, date, matchId);
    const pre = priceOf(zone, type);
    // Taxed venue + somewhere to draw it → show the real total first; otherwise straight to checkout.
    if (anchor && taxFor(pre, pct) > 0) {
      if (blockIfSalesPaused(cfg, root)) return;
      confirmWithTax(anchor, what || `${typeLabel(type)} · ${fmtT(slot.slotStart)} – ${fmtT(slot.slotEnd)}`, pre, pct, go);
      return;
    }
    go();
  };
  // The logged-in customer's purchased streams (venue-wide), rendered at the bottom of either path below.
  const appendMine = () => {
    if (!auth.isLoggedIn) return;
    const mineHost = h('div', { style: 'margin-top:10px' });
    root.append(mineHost);
    void renderMyPurchases(mineHost, api, { id: zone.venueId, name: zone.venueName }, (sessionId) => {
      void showEntitled(root, cfg, api, auth, sessionId, null, null, 'Watch');
    }).then((st) => {
      // Secondary section — a load failure shouldn't look like an empty library.
      if (st === 'error') mineHost.append(h('div', { class: 'e365-alert warn' }, 'We couldn’t load your purchased streams right now — refresh to retry.'));
    });
  };

  root.append(
    h('h3', undefined, `Watch · ${zone.name}`),
    h('div', { class: 'e365-muted', style: 'margin:-6px 0 8px' }, zone.venueName),
  );

  // Let the customer switch courts if they picked the wrong one.
  const changeCourt = h('a', { href: '#', style: 'font-size:.82rem; display:inline-block; margin:-2px 0 8px' }, '‹ Change court') as HTMLAnchorElement;
  changeCourt.addEventListener('click', (e) => { e.preventDefault(); void renderZonePicker(root, cfg, api, auth, date, zoneId); });
  root.append(changeCourt);

  if (notice) root.append(h('div', { class: 'e365-alert warn' }, notice));
  // The one rate note for every price below (now card, hourly grid, game recordings). Null = untaxed.
  const taxLine = taxNoteEl(pct);
  if (taxLine) root.append(taxLine);

  // ── LIVE NOW — games being scored right now (walk-in or scheduled). Buying live on
  // this court is the existing current-hour stream purchase; other courts get a jump link. ──
  for (const lg of liveGames) {
    const label = lg.title && lg.title.trim()
      ? lg.title
      : `${lg.homeTeam} ${lg.homeScore ?? 0}–${lg.awayScore ?? 0} ${lg.awayTeam}`;
    const here = lg.zoneId === zoneId;
    const banner = h('div', { class: 'e365-alert warn', style: 'display:flex;align-items:center;gap:8px;flex-wrap:wrap' },
      h('span', undefined, `🔴 LIVE NOW — ${label}${here ? '' : ` · ${lg.zoneName ?? 'another court'}`}`));
    if (here && zone.primaryCamera && date === todayLocal()) {
      const slot = currentRoundedSlot(zone);
      if (slot) {
        // Buy THIS GAME: access from the game's start until the scoreboard ends it — whatever the length —
        // for the same price as a 1-hour block. (The fixed 1-hour blocks are still available in the grid below.)
        const b = h('button', { class: 'e365-btn', type: 'button', style: 'margin:0;flex:0 0 auto' },
          `Watch this game · ${money(zone.liveStreamPrice)}`) as HTMLButtonElement;
        // Its own wrapper so the tax confirm step can replace just the button, not the whole banner.
        const bWrap = h('div', { style: 'flex:0 0 auto' }, b);
        b.addEventListener('click', () => buy(slot, 'LiveStream', bWrap, lg.matchId, `Watch this game · ${label}`));
        banner.append(bWrap);
        banner.append(h('span', { class: 'e365-muted', style: 'font-size:.78rem;flex:1 1 100%' },
          'The whole game — ends when the scoreboard does. Prefer a set hour? Pick a 1-hour block below.'));
      }
    } else if (!here) {
      const a = h('a', { href: '#', style: 'font-size:.85rem' }, 'Go to court ›') as HTMLAnchorElement;
      a.addEventListener('click', (e) => { e.preventDefault(); void load(root, cfg, api, auth, lg.zoneId, date); });
      banner.append(a);
    }
    root.append(banner);
  }

  // No camera installed on this court → it can't stream at all. Say so plainly + skip the buy UI. (The
  // picker now hides camera-less courts; this guards the per-court QR/link path to one.)
  if (!zone.primaryCamera) {
    root.append(h('div', { class: 'e365-alert info' }, 'This court isn’t set up for live streaming yet — check back soon.'));
    appendMine();
    root.append(footer());
    return;
  }

  // ── Quick-buy the CURRENT hour (only on today) — start snapped to the nearest 15-min slot ──
  if (date === todayLocal()) {
    const nowCard = renderNowCard(zone, buy);
    if (nowCard) root.append(nowCard);
  }

  // ── Date picker ──
  const dateInput = h('input', { type: 'date', value: date, style: 'margin-bottom:6px' }) as HTMLInputElement;
  dateInput.addEventListener('change', () => { if (dateInput.value) void load(root, cfg, api, auth, zoneId, dateInput.value); });
  root.append(h('label', undefined, 'Date'), dateInput);

  // ── Hourly grid (the time-frame picker) ──
  const groups = hourlyGroups(zone, date);
  if (groups.length === 0) {
    root.append(h('div', { class: 'e365-alert info' },
      zone.hasOnlineCamera
        ? 'No streaming times for this date — try another day.'
        : 'The camera on this court is offline right now, so live isn’t available. Recordings will show here once they’re ready.'));
  } else {
    const grid = h('div', { class: 'e365-hour-grid' });
    for (const [hour, slots] of groups) grid.appendChild(renderHourCard(zone, hour, slots, buy));
    root.append(grid);
  }

  // ── Whole-game recordings (scoreboard-tracked) → platform first-party ──
  appendGames(root, 'Today’s games', games, (g) => goRecording(cfg, g.matchId));
  appendGames(root, 'Recent recordings', recent, (g) => goRecording(cfg, g.matchId));

  // Already-purchased streams at the BOTTOM (the buy options above are the primary action).
  appendMine();

  root.append(
    h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:8px' }, 'You’ll sign in to your 365 account to watch & pay. Cancel anytime before checkout.'),
    footer(),
  );
}

/** Group the day's 1-hour windows by LOCAL start-hour, filtered to the zone's operating schedule + relevance. */
function hourlyGroups(zone: ZoneDetail, date: string): [number, WatchSlot[]][] {
  const map = new Map<number, WatchSlot[]>();
  for (const s of zone.timeSlots) {
    if (s.isOutsideSchedule) continue; // outside the zone's operating hours
    const hour = asDate(s.slotStart).getHours();
    (map.get(hour) ?? map.set(hour, []).get(hour)!).push(s);
  }
  const isPastDate = date < todayLocal();
  const out: [number, WatchSlot[]][] = [];
  for (const [hour, slots] of map) {
    slots.sort((a, b) => a.slotStart.localeCompare(b.slotStart));
    // Past dates: only hours that actually have a recording. Today/future: any hour with a buyable slot.
    const keep = isPastDate
      ? slots.some((s) => s.recordingAvailable)
      : slots.some((s) => (s.isAvailable && !s.isPast) || s.recordingAvailable || s.hasActiveSession);
    if (keep) out.push([hour, slots]);
  }
  out.sort((a, b) => a[0] - b[0]);
  return out;
}

/**
 * D9: one camera films the whole floor, so on a shared court the video contains every game on it.
 * Said before the purchase rather than discovered after it. Rendered ONLY when the court is actually
 * shared - an ordinary court is the degenerate case and gains no extra wording. Sub-court letters are
 * deliberately absent: they would imply the buyer can pick one game out of the several, which the
 * single stream product does not let them do.
 */
function sharedFloorNote(slot: WatchSlot): HTMLElement | null {
  if (!slot.gamesOnCourt || slot.gamesOnCourt < 2) return null;
  const prefix = slot.sport ? `${slot.sport} · ` : '';
  return h('div', { style: 'font-size:.78rem; color:var(--e365-primary)' },
    `${prefix}${slot.gamesOnCourt} games on court`);
}

/** One hour card: the :00/:15/:30/:45 offset picker + the option buttons for the selected 1-hour window. */
function renderHourCard(zone: ZoneDetail, hour: number, slots: WatchSlot[], buy: BuyFn): HTMLElement {
  const card = h('div', { class: 'e365-hour' });
  card.append(h('div', { class: 'e365-hour-h' }, fmtHour(hour)));
  const body = h('div');
  card.append(body);

  // Default to the current quarter (live), else the first upcoming offset, else :00.
  let sel = slots.findIndex((s) => s.isCurrentSlot);
  if (sel < 0) sel = Math.max(0, slots.findIndex((s) => !s.isPast));

  const draw = () => {
    clear(body);
    const slot = slots[sel] ?? slots[0];

    if (slots.length > 1) {
      const chips = h('div', { class: 'e365-slots', style: 'margin:6px 0' });
      slots.forEach((s, i) => {
        const min = asDate(s.slotStart).getMinutes();
        const chip = h('button', { class: 'e365-slot', type: 'button', style: 'padding:3px 9px;font-size:.78rem' },
          `:${String(min).padStart(2, '0')}`) as HTMLButtonElement;
        chip.setAttribute('aria-pressed', String(i === sel));
        chip.addEventListener('click', () => { sel = i; draw(); });
        chips.append(chip);
      });
      body.append(chips);
    }

    body.append(h('div', { class: 'e365-muted', style: 'font-size:.78rem' }, `${fmtT(slot.slotStart)} – ${fmtT(slot.slotEnd)}`));
    if (slot.matchLabel) body.append(h('div', { class: 'e365-muted', style: 'font-size:.78rem' }, slot.matchLabel));
    const shared = sharedFloorNote(slot);
    if (shared) body.append(shared);

    const opts = h('div', { style: 'display:flex;flex-direction:column;gap:5px;margin-top:6px' });
    const optBtn = (label: string, type: PType, primary?: boolean) => {
      const b = h('button', { class: primary ? 'e365-btn' : 'e365-opt', type: 'button' }, label) as HTMLButtonElement;
      b.addEventListener('click', () => buy(slot, type, opts));
      opts.append(b);
    };

    if (slot.isAvailable && !slot.isPast) {
      optBtn(`Live · ${money(zone.liveStreamPrice)}`, 'LiveStream');
      optBtn(`Recording · ${money(zone.recordingPricePerHour)}`, 'Recording');
      optBtn(`Bundle · ${money(bundlePrice(zone))} · save 20%`, 'Bundle', true);
    } else if (slot.recordingAvailable) {
      optBtn(`Recording · ${money(zone.recordingPricePerHour)}`, 'Recording', true);
    } else {
      opts.append(h('div', { class: 'e365-muted', style: 'font-size:.8rem' }, slot.isPast ? 'No recording' : 'Unavailable'));
    }
    body.append(opts);
  };
  draw();
  return card;
}

/** The current 1-hour window, start snapped to the NEAREST 15-min slot (2:04→2:00, 2:08→2:15). */
function currentRoundedSlot(zone: ZoneDetail): WatchSlot | null {
  // Prefer the server-authoritative "current" slot — the one whose 1-hour window covers RIGHT NOW.
  // Buying it streams immediately. (Rounding to the NEAREST quarter-hour would round UP in the last
  // ~7.5 min of a slot — e.g. 8:58 → 9:00 — buying a slot that hasn't started, so the player sits on
  // "waiting until 9:00" even though the game is already live. Always anchor to now, never the future.)
  const cur = zone.timeSlots.find((s) => s.isCurrentSlot && s.isAvailable && !s.isOutsideSchedule);
  if (cur) return cur;
  // Fallback (no flagged current slot): nearest buyable slot at/near the floored quarter-hour.
  const q = 15 * 60 * 1000;
  const floored = Math.floor(Date.now() / q) * q;
  let best: WatchSlot | null = null;
  let bestDiff = Infinity;
  for (const s of zone.timeSlots) {
    if (s.isOutsideSchedule || !s.isAvailable || s.isPast) continue;
    const diff = Math.abs(asDate(s.slotStart).getTime() - floored);
    if (diff < bestDiff) { bestDiff = diff; best = s; }
  }
  return best && bestDiff <= 50 * 60 * 1000 ? best : null;
}

/** Prominent quick-buy for the current hour, with all three purchase options. */
function renderNowCard(zone: ZoneDetail, buy: BuyFn): HTMLElement | null {
  const slot = currentRoundedSlot(zone);
  if (!slot) return null;
  const live = slot.hasActiveSession || zone.primaryCamera?.isStreaming;
  const card = h('div', { class: 'e365-now' },
    h('div', { class: 'e365-now-h' }, `${live ? '🔴' : '▶'} Buy now · ${fmtT(slot.slotStart)}–${fmtT(slot.slotEnd)}`));
  if (slot.matchLabel) card.append(h('div', { class: 'e365-muted', style: 'font-size:.82rem' }, slot.matchLabel));
  const sharedNow = sharedFloorNote(slot);
  if (sharedNow) card.append(sharedNow);
  const opts = h('div', { style: 'display:flex; flex-wrap:wrap; gap:6px; margin-top:8px' });
  const ob = (label: string, type: PType, primary?: boolean) => {
    const b = h('button', { class: primary ? 'e365-btn' : 'e365-opt', type: 'button', style: 'flex:1 1 30%; margin-top:0' }, label) as HTMLButtonElement;
    b.addEventListener('click', () => buy(slot, type, opts));
    opts.append(b);
  };
  ob(`Live · ${money(zone.liveStreamPrice)}`, 'LiveStream');
  ob(`Recording · ${money(zone.recordingPricePerHour)}`, 'Recording');
  ob(`Bundle · ${money(bundlePrice(zone))}`, 'Bundle', true);
  card.append(opts);
  return card;
}

// ── Purchase: ensure login (cross-domain), then call the API directly. Stripe/return is server-side embed-aware. ──

function startPurchase(
  root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, zoneId: string, slot: WatchSlot, type: PType, date: string,
  matchId?: string, // set → LIVE-GAME purchase: access from game start to Final (not a fixed hour)
): void {
  if (blockIfSalesPaused(cfg, root)) return;
  if (!auth.isLoggedIn) {
    auth.redirectToLogin({ t: 'watch', zoneId, slotStart: slot.slotStart, type, matchId } as PendingBuy); // full-page; resumes via peekPending
    return;
  }
  renderLoading(root, 'Setting up your purchase…');
  void doPurchase(root, cfg, api, auth, zoneId, slot.slotStart, type, date, matchId);
}

async function doPurchase(
  root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, zoneId: string, slotStart: string, type: PType, date: string,
  matchId?: string,
): Promise<void> {
  try {
    // Funnel + attribution: `wa` rides in the purchase body (optional; the API ignores it when absent).
    track('stream_start', 'watch');
    const wa = trackIds();
    const r = await api.post<TimeSlotPurchaseResult>('/api/timeslots/purchase',
      { zoneId, slotStart, purchaseType: type, source: 'embed', matchId, ...(wa ? { wa } : {}) });
    if (r.requiresPayment && r.checkoutUrl) {
      window.location.href = r.checkoutUrl; // Stripe → returns to THIS venue site (/payment/success) server-side
      return;
    }
    // Free access granted (e.g. 100% code) → this venue site's payment-success page.
    window.location.href = `${sitePage('paymentSuccess')}?purchase_id=${encodeURIComponent(r.purchaseId)}`;
  } catch (e) {
    if (e instanceof AuthRequiredError) {
      auth.redirectToLogin({ t: 'watch', zoneId, slotStart, type, matchId } as PendingBuy);
      return;
    }
    void load(root, cfg, api, auth, zoneId, date, friendlyError(e));
  }
}

/** Pull the server's {message} out of the Api error string for a friendly inline notice. */
function friendlyError(e: unknown): string {
  const raw = e instanceof Error ? e.message : '';
  const m = raw.match(/\{.*"message"\s*:\s*"([^"]+)"/);
  if (m) return m[1];
  if (/\b409\b/.test(raw)) return 'You already have access to this time slot.';
  if (/\b400\b/.test(raw)) return 'That time slot can’t be purchased right now.';
  return 'Purchase couldn’t be completed. Please try again.';
}

/** Cloudflare Stream manifest URL → iframe player URL (strip query, swap …/manifest/… → …/iframe). */
function cfEmbed(playbackUrl: string): string {
  const base = playbackUrl.split('?')[0];
  const i = base.indexOf('/manifest/');
  return i >= 0 ? `${base.slice(0, i)}/iframe` : base;
}

/** Stripe returned to the venue site (/payment/success) → confirm the purchase, then show an in-widget player. */
async function renderPaymentSuccess(
  root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, purchaseId: string, stripeSession: string,
): Promise<void> {
  renderLoading(root, 'Confirming your purchase…');
  if (!auth.isLoggedIn) {
    // Stripe can return in a fresh tab (or an in-app browser) where the sessionStorage token doesn't
    // exist — the customer has just PAID, so never dead-end them: sign in round-trips back to this
    // exact URL (purchase_id + session_id intact) and the confirm resumes.
    clear(root);
    root.append(
      h('h3', undefined, 'Payment received ✓'),
      h('div', { class: 'e365-alert info' }, 'Sign in on this device to start watching — your purchase is safe on your 365 account.'),
    );
    const login = h('button', { class: 'e365-btn' }, 'Sign in to watch') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin());
    root.append(login, footer());
    return;
  }
  let r: TimeSlotPurchaseResult;
  try {
    r = await api.post<TimeSlotPurchaseResult>(`/api/timeslots/${encodeURIComponent(purchaseId)}/confirm`, stripeSession);
  } catch {
    renderError(root, 'We couldn’t confirm your purchase yet — it may still be processing. Tap to retry.',
      () => void renderPaymentSuccess(root, cfg, api, auth, purchaseId, stripeSession));
    return;
  }
  await showEntitled(root, cfg, api, auth, r.sessionId ?? null, r.playbackUrl ?? null, r.message ?? null);
}

function watchOn365Btn(cfg: EmbedConfig, sessionId: string): HTMLElement {
  return h('a', { href: `${platformBase(cfg)}/watch/${sessionId}`, target: '_blank', rel: 'noopener',
    class: 'e365-btn', style: 'margin-top:8px; display:flex; text-decoration:none' }, 'Watch on 365 →');
}
/** Per-tab device id (same idea as the web Watch page's sessionStorage 'watchDeviceId'). */
function watchDeviceId(): string {
  try {
    let id = sessionStorage.getItem('e365_watchDeviceId');
    if (!id) {
      id = (typeof crypto !== 'undefined' && crypto.randomUUID) ? crypto.randomUUID() : `d-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      sessionStorage.setItem('e365_watchDeviceId', id);
    }
    return id;
  } catch { return `d-${Date.now()}`; }
}

/**
 * Embed the Cloudflare player AND run the one-stream-per-account heartbeat — identical enforcement to the
 * web Watch page. A 12s heartbeat POSTs sessions/{id}/heartbeat with this tab's deviceId; the server keys
 * the active viewer by USER, so when the same account starts watching elsewhere, this device's next beat
 * returns kicked → we stop playback (preventing login sharing / simultaneous streams).
 */
function showPlayer(host: HTMLElement, api: Api, cfg: EmbedConfig, sessionId: string, playbackUrl: string): void {
  const dev = watchDeviceId();
  let stopped = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  // The iframe lives in videoWrap (cleared on kick/resume); the score overlay sits in overlayLayer, a
  // sibling positioned over the video, so it survives video re-renders.
  host.style.position = 'relative';
  const videoWrap = h('div');
  const overlayLayer = h('div');
  host.append(videoWrap, overlayLayer);

  // Claim the active-viewer slot for THIS device (newest-wins). GET sessions/{id}?deviceId= is what the
  // server uses to RegisterViewer + kick the previously-active device; the heartbeat alone only REPORTS a
  // kick (ProcessHeartbeat), it never takes over. So we must claim on first play AND on "Resume here".
  const claim = async (): Promise<void> => {
    try { await api.get<unknown>(`/api/timeslots/sessions/${sessionId}?deviceId=${encodeURIComponent(dev)}`); } catch { /* best-effort */ }
  };

  const renderVideo = () => {
    clear(videoWrap);
    videoWrap.append(h('iframe', {
      src: `${cfEmbed(playbackUrl)}?autoplay=true&controls=true`,
      allow: 'autoplay; encrypted-media; fullscreen', allowfullscreen: 'true',
    }));
  };
  const onKicked = (msg: string, resumable: boolean) => {
    stopped = true;
    if (timer) clearTimeout(timer);
    clear(videoWrap);
    videoWrap.append(h('div', { class: 'e365-alert warn' }, msg));
    // "Resume on this device" only makes sense for a genuine another-device takeover. When the access
    // window has ENDED (reason 'expired'), there's nothing to resume — showing the button just reloads
    // the page and confuses people, so we omit it (the "Watch on 365" / "Browse more streams" options
    // below remain available for the recording).
    if (resumable) {
      const resume = h('button', { class: 'e365-btn', style: 'margin-top:8px' }, 'Resume on this device') as HTMLButtonElement;
      resume.addEventListener('click', () => { stopped = false; void (async () => { await claim(); renderVideo(); void beat(); })(); });
      videoWrap.append(resume);
    }
  };
  const beat = async (): Promise<void> => {
    if (stopped) return;
    try {
      const r = await api.post<{ kicked?: boolean; message?: string; reason?: string }>(
        `/api/timeslots/sessions/${sessionId}/heartbeat`, { deviceId: dev });
      if (r?.kicked) {
        const ended = r.reason === 'expired' || r.reason === 'ended';
        onKicked(r.message || 'This stream is playing on another device.', !ended);
        return;
      }
    } catch { /* transient — keep beating */ }
    if (!stopped) timer = setTimeout(() => void beat(), 12000);
  };

  // Claim on first play so opening the stream here takes over any other device (newest-wins), then play.
  void (async () => { await claim(); renderVideo(); void beat(); })();

  // If this session is streaming a scoreboard-tracked game, layer the live score over the player.
  void (async () => {
    try {
      const s = await api.get<StreamSession>(`/api/timeslots/sessions/${sessionId}`);
      if (s?.scoreOverlayMatchId) attachScoreOverlay(overlayLayer, cfg, s.scoreOverlayMatchId);
    } catch { /* no overlay — player unaffected */ }
  })();
}

/** Show the entitled stream: play in-widget if ready, else poll the session until the live stream starts. */
async function showEntitled(
  root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, sessionId: string | null, playbackUrl: string | null, message: string | null,
  title = 'You’re in! 🎉',
): Promise<void> {
  const heading = () => {
    clear(root);
    root.append(h('h3', undefined, title));
    if (message) root.append(h('div', { class: 'e365-alert info' }, message));
  };
  const browseMore = () => {
    const back = h('button', { class: 'e365-slot', style: 'width:100%; margin-top:8px' }, 'Browse more streams') as HTMLButtonElement;
    back.addEventListener('click', () => {
      try { history.replaceState(null, '', location.pathname); } catch { /* ignore */ }
      void load(root, cfg, api, auth, null, todayLocal());
    });
    return back;
  };

  if (playbackUrl) {
    heading();
    const playerHost = h('div');
    root.append(playerHost);
    if (sessionId) showPlayer(playerHost, api, cfg, sessionId, playbackUrl);
    else playerHost.append(h('iframe', { src: `${cfEmbed(playbackUrl)}?autoplay=true&controls=true`, allow: 'autoplay; encrypted-media; fullscreen', allowfullscreen: 'true' }));
    if (sessionId) root.append(watchOn365Btn(cfg, sessionId));
    root.append(browseMore(), footer());
    return;
  }
  if (!sessionId) {
    heading();
    root.append(h('div', { class: 'e365-alert warn' }, 'Saved to “My Purchases” on your 365 account — your recording will be ready shortly.'));
    root.append(browseMore(), footer());
    return;
  }

  // Fetch the session and decide what to do. The API resolves the RECORDING for a past bundle/recording
  // purchase, so `playbackUrl` may be a live OR a recording URL — either way we play it in-widget. If the
  // recording's download window has expired (or the session ended/failed), the API returns a terminal
  // status + a message: we show THAT immediately instead of faking a "loading…" that times out. Only a
  // genuinely spinning-up live stream gets the "Starting your live stream…" copy + the ~60s poll.
  const terminal = /expired|ended|failed|cancelled/i;
  const playNow = (url: string) => {
    heading();
    const playerHost = h('div');
    root.append(playerHost);
    showPlayer(playerHost, api, cfg, sessionId!, url);
    root.append(watchOn365Btn(cfg, sessionId!), browseMore(), footer());
  };

  renderLoading(root, 'Loading…');
  let spinning = false;
  for (let i = 0; i < 20; i++) {
    let s: StreamSession | null = null;
    try { s = await api.get<StreamSession>(`/api/timeslots/sessions/${sessionId}`); } catch { /* transient — retry */ }
    if (s?.playbackUrl && !/pending|starting/i.test(s.status || '')) { playNow(s.playbackUrl); return; }
    // Terminal, non-playable state (e.g. recording download window expired) → say so right away.
    if (s && !s.playbackUrl && terminal.test(s.status || '')) {
      heading();
      root.append(h('div', { class: 'e365-alert warn' }, s.statusMessage || 'This stream is no longer available.'));
      root.append(browseMore(), footer());
      return;
    }
    if (s && /pending|starting/i.test(s.status || '') && !spinning) { spinning = true; renderLoading(root, 'Starting your live stream…'); }
    await new Promise((res) => setTimeout(res, 3000));
  }
  // Timed out while a live stream was genuinely still spinning up — hand off to the platform player.
  heading();
  root.append(h('div', { class: 'e365-alert warn' }, 'Your live stream is still starting — open it on 365 below.'));
  root.append(watchOn365Btn(cfg, sessionId), browseMore(), footer());
}

/** Label for an owned-purchase row, e.g. "Office Court · Jun 24, 9:00 PM · Live Stream". */
function ownedLabel(p: MyPurchase): string {
  const when = p.accessStart ? asDate(p.accessStart).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';
  const t = typeof p.type === 'string' ? p.type.replace(/([a-z])([A-Z])/g, '$1 $2') : '';
  return [p.zoneName || 'Court', when, t].filter(Boolean).join(' · ');
}

/**
 * "Your streams & recordings" — the logged-in customer's owned, watchable purchases for THIS venue.
 * Returns a status so callers can tell an EMPTY library from a FAILED load (an outage must never
 * render as "you own nothing"). Venue match is by venueId (rename-proof) with venueName fallback
 * for rows from an older API that doesn't send venueId yet.
 */
async function renderMyPurchases(
  host: HTMLElement, api: Api, venue: { id?: string; name?: string }, onWatch: (sessionId: string) => void,
): Promise<'ok' | 'empty' | 'error'> {
  let all: MyPurchase[];
  try { all = (await api.get<MyPurchase[]>('/api/purchases')) || []; } catch { return 'error'; }
  const vid = (venue.id || '').toLowerCase();
  const matchesVenue = (p: MyPurchase) => p.venueId && vid
    ? p.venueId.toLowerCase() === vid
    : (!venue.name || p.venueName === venue.name);
  const mine = all.filter((p) => p.streamingSessionId && p.isPaid !== false && matchesVenue(p));
  if (!mine.length) return 'empty';

  // TOP = current or future purchases (active-now first, then upcoming). BOTTOM = anything passed
  // (watchable recordings above finished live-only). Use the 1-hour slot if no explicit end time.
  const now = Date.now();
  const startMs = (p: MyPurchase) => (p.accessStart ? asDate(p.accessStart).getTime() : 0);
  const endMs = (p: MyPurchase) =>
    p.accessEnd ? asDate(p.accessEnd).getTime() : (p.accessStart ? asDate(p.accessStart).getTime() + 3600000 : Infinity);
  const isLiveOnly = (p: MyPurchase) => { const t = String(p.type ?? '').toLowerCase(); return t.includes('live') && !t.includes('bundle'); };
  const isPassed = (p: MyPurchase) => endMs(p) < now;
  const rank = (p: MyPurchase): number => {
    if (!isPassed(p)) return startMs(p) <= now ? 0 : 1;  // 0 = active now, 1 = future (both at top)
    return isLiveOnly(p) ? 3 : 2;                          // 2 = passed recording, 3 = finished live-only (bottom)
  };
  mine.sort((a, b) => (rank(a) - rank(b)) || (endMs(b) - endMs(a))); // most recent first within a group

  clear(host);
  host.append(h('label', { style: 'margin-top:0' }, 'Your streams & recordings'));
  const list = h('div', { class: 'e365-slots', style: 'flex-direction:column' });
  for (const p of mine.slice(0, 15)) {
    // A LIVE-only purchase keeps no recording — once its window has passed there's nothing to play.
    // Recording / Bundle keep the recording, so they stay watchable.
    const ended = isLiveOnly(p) && isPassed(p);
    const item = h('div');
    if (ended) {
      item.append(h('div', { class: 'e365-slot', style: 'display:block; text-align:left; opacity:.5; cursor:default' },
        `${ownedLabel(p)} · Live stream ended`));
    } else {
      const btn = h('button', { class: 'e365-slot', type: 'button', style: 'display:block; width:100%; text-align:left' }, `▶ ${ownedLabel(p)}`) as HTMLButtonElement;
      btn.addEventListener('click', () => onWatch(p.streamingSessionId!));
      item.append(btn);
    }
    // Receipt under each purchase (proof of purchase + order #).
    const r = receipt({
      orderNumber: p.orderNumber, amount: p.amount, taxAmount: p.taxAmount, currency: p.currency,
      paidLabel: p.paidAt ? fmtDateShort(p.paidAt) : null,
      place: [p.zoneName, p.venueName].filter(Boolean).join(' · '),
    });
    if (r) item.append(r);
    list.append(item);
  }
  host.append(list);
  return 'ok';
}

/** Render scoreboard-tracked games as one-click whole-game recording launches (platform first-party). */
function appendGames(root: HTMLElement, title: string, list: RecentGame[], onPick: (g: RecentGame) => void): void {
  if (!list || list.length === 0) return;
  root.append(h('label', undefined, title));
  const wrap = h('div', { class: 'e365-slots' });
  for (const g of list) {
    const teams = (g.homeTeam || g.awayTeam) ? `${g.homeTeam || 'Home'} vs ${g.awayTeam || 'Away'}` : 'Game';
    const score = (g.homeScore != null && g.awayScore != null) ? ` · ${g.homeScore}–${g.awayScore}` : '';
    const where = g.zoneName ? ` · ${g.zoneName}` : '';
    const price = g.price != null ? ` · ${money(g.price)}` : '';
    wrap.append(h('button', { class: 'e365-slot', type: 'button', style: 'text-align:left',
      onClick: () => onPick(g) }, `${teams}${score}${where} · Watch recording${price}`));
  }
  root.append(wrap);
}

/** Court picker — used when no ?zone= is supplied, or via "Change court". Preselects the current zone. */
async function renderZonePicker(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, date: string, currentZoneId?: string): Promise<void> {
  renderLoading(root, 'Watch live & recordings');
  let venue: { id: string; name: string; zones?: { id: string; name: string; isAvailableForStreaming?: boolean; primaryCamera?: unknown | null }[] };
  try {
    venue = await api.get(`/api/browse/venues/${cfg.venueId}`);
  } catch {
    renderError(root, 'We couldn’t load streams. Please try again.', () => void renderZonePicker(root, cfg, api, auth, date, currentZoneId));
    return;
  }
  // Only courts with a camera installed (online OR offline) can stream — hide the rest so the picker
  // isn't full of dead courts. (A venue can flag IsAvailableForStreaming before a camera is installed.)
  const zones = (venue.zones || []).filter((z) => z.isAvailableForStreaming !== false && z.primaryCamera != null);
  clear(root);
  if (zones.length === 0) {
    root.append(h('h3', undefined, 'Watch live & recordings'),
      h('div', { class: 'e365-alert info' }, 'Live streaming isn’t set up at this venue yet — check back soon.'), footer());
    return;
  }
  const sel = h('select', undefined,
    h('option', { value: '' }, 'Choose a court…'),
    ...zones.map((z) => h('option', { value: z.id }, z.name))) as HTMLSelectElement;
  if (currentZoneId) sel.value = currentZoneId;
  sel.addEventListener('change', () => { if (sel.value) void load(root, cfg, api, auth, sel.value, date); });
  root.append(h('h3', undefined, 'Watch live & recordings'));
  // Court picker FIRST, in a highlighted box — buying is the primary action.
  const pick = h('div', { class: 'e365-pickbox' });
  pick.append(
    h('label', undefined, 'Choose a court to watch or buy'),
    sel,
    h('div', { class: 'e365-muted', style: 'font-size:.8rem; margin-top:6px' }, 'Pick a court to buy a live stream or recording.'),
  );
  root.append(pick);
  // Already-purchased streams below, as a secondary action.
  if (auth.isLoggedIn) {
    const mineHost = h('div', { style: 'margin-top:14px' });
    root.append(mineHost);
    void renderMyPurchases(mineHost, api, { id: venue.id, name: venue.name }, (sessionId) => {
      void showEntitled(root, cfg, api, auth, sessionId, null, null, 'Watch');
    }).then((st) => {
      if (st === 'error') mineHost.append(h('div', { class: 'e365-alert warn' }, 'We couldn’t load your purchased streams right now — refresh to retry.'));
    });
  }
  root.append(footer());
}
